import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IApiChannel, ChannelName, ChannelType } from '../../types';
import { CHANNEL_INFO, T_KEYS } from '../../constants';
import { mapChannelTypeToName } from '../../services/channels.service';
import {
  ChannelCardContainer,
  ChannelCardHeader,
  ChannelLogos,
  MsoolLogo,
  ConnectionLine,
  ChannelLogo,
  StatusBadge,
  StatusDot,
  ChannelStats,
  StatItem,
  StatDivider,
  ConnectButton,
} from '../../styles';

// Import channel SVGs
import AIRBNB from '@assets/svg/airbnb.svg?react';
import GATHERN from '@assets/svg/gathren.svg?react';
import BOOKING from '@assets/svg/BookingLogo.svg?react';
import MsoolIcon from '@assets/svg/MsoolIcon.svg?react';

interface IChannelCardProps {
  channel: IApiChannel;
  onConnect: (channelName: ChannelName) => void;
  onManage?: (channelName: ChannelName) => void;
}

const ChannelCard: FC<IChannelCardProps> = memo(({ channel, onConnect, onManage }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  // Derive channel name from channelType
  const channelName = useMemo(() => mapChannelTypeToName(channel.channelType), [channel.channelType]);
  
  // Derive status from isConnected
  const status = channel.isConnected ? 'connected' : 'not_connected';

  const getStatusText = () => {
    if (channel.isConnected) {
      return t(T_KEYS.CONNECTED);
    }
    return t(T_KEYS.NOT_CONNECTED);
  };

  const getChannelLogo = () => {
    switch (channel.channelType) {
      case ChannelType.Airbnb:
        return <AIRBNB />;
      case ChannelType.Booking:
        return <BOOKING />;
      case ChannelType.Gathern:
        return <GATHERN />;
      default:
        return null;
    }
  };

  const handleButtonClick = () => {
    if ((channel.connectedAccounts?.length ?? 0) && onManage) {
      onManage(channelName);
    } else {
      onConnect(channelName);
    }
  };

  const channelInfo = CHANNEL_INFO[channelName];

  return (
    <ChannelCardContainer>
      <ChannelCardHeader>
        <ChannelLogos>
          <ChannelLogo $bgColor={channel.isConnected ? undefined : '#f9fafb'}>
            {getChannelLogo()}
          </ChannelLogo>
          <ConnectionLine />
          <MsoolLogo>
            <MsoolIcon />
          </MsoolLogo>
        </ChannelLogos>
      </ChannelCardHeader>
      <div className='w-full flex justify-center'>
        <StatusBadge $status={status}>
          <StatusDot $status={status} />
          {getStatusText()}
        </StatusBadge>
      </div>
      <div className=''>
        <ChannelStats>
          <StatItem>
            {channel.numberOfConnectedAccounts} {t(T_KEYS.CONNECTED_ACCOUNTS)}
          </StatItem>
          <StatDivider>•</StatDivider>
          <StatItem>
            {channel.importedUnits} {t(T_KEYS.IMPORTED_UNITS)}
          </StatItem>
        </ChannelStats>
        {channel.numberOfDisconnectedAccounts > 0 && (
          <StatItem style={{ color: '#f04438', textAlign: 'center' }}>
            {channel.numberOfDisconnectedAccounts} {t(T_KEYS.DISCONNECTED_ACCOUNTS)}
          </StatItem>
        )}
      </div>
      <ConnectButton
        $status={(channel.connectedAccounts?.length ?? 0) > 0 ? 'connected' : 'not_connected'}
        onClick={handleButtonClick}
      >
        {(channel.connectedAccounts?.length ?? 0) > 0
          ? t(T_KEYS.MANAGE_ACCOUNTS)
          : `${t(T_KEYS.CONNECT_WITH)} ${isRtl ? channelInfo.displayNameAr : channelInfo.displayName}`}
      </ConnectButton>
    </ChannelCardContainer>
  );
});

ChannelCard.displayName = 'ChannelCard';

export default ChannelCard;
